"use client";

import { useRef, useCallback } from 'react';

/**
 * useTapRipple — creates a ripple effect at the tap/click position
 * Returns a ref to attach to the container and a handler to call on pointer down
 */
export function useTapRipple() {
  const containerRef = useRef<HTMLElement | null>(null);

  const triggerRipple = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const el = containerRef.current ?? (e.currentTarget as HTMLElement);
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = Math.max(rect.width, rect.height) * 2;

    const ripple = document.createElement('span');
    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
      width: ${size}px;
      height: ${size}px;
      left: ${x - size / 2}px;
      top: ${y - size / 2}px;
      background: rgba(0, 163, 255, 0.18);
      transform: scale(0);
      animation: mobile-ripple 0.55s ease-out forwards;
      z-index: 9999;
    `;

    el.style.position = el.style.position || 'relative';
    el.style.overflow = 'hidden';
    el.appendChild(ripple);

    ripple.addEventListener('animationend', () => ripple.remove());
  }, []);

  return { containerRef, triggerRipple };
}

/**
 * usePressAnimation — returns className string and event handlers for press scale effect
 */
export function usePressAnimation(scale = 0.97) {
  const ref = useRef<HTMLElement | null>(null);

  const onPointerDown = useCallback(() => {
    if (ref.current) {
      ref.current.style.transform = `scale(${scale})`;
      ref.current.style.transition = 'transform 0.12s ease';
    }
  }, [scale]);

  const onPointerUp = useCallback(() => {
    if (ref.current) {
      ref.current.style.transform = 'scale(1)';
      ref.current.style.transition = 'transform 0.2s ease';
    }
  }, []);

  const onPointerLeave = useCallback(() => {
    if (ref.current) {
      ref.current.style.transform = 'scale(1)';
      ref.current.style.transition = 'transform 0.2s ease';
    }
  }, []);

  return { ref, onPointerDown, onPointerUp, onPointerLeave };
}
